import { toast } from "sonner";
import { toApiFailure } from "./errors";

/**
 * German fallbacks for the status codes the write paths surface. The caller
 * resolves these from i18n (t("errors.*")) and passes them in, so this module
 * stays free of any i18n coupling while the messages stay German (NF-021).
 */
export interface ErrorMessages {
  precondition: string;
  forbidden: string;
  conflict: string;
  generic: string;
}

/**
 * Turn an apiFetch failure into a toast. Prefers the server's RFC-7807 German
 * `detail` when present, else the localized fallback. On 412 (optimistic-
 * concurrency clash, ADR-FE-006) it also invokes `onConflict` so the caller can
 * refetch the stale entity.
 */
export function notifyApiError(
  err: unknown,
  messages: ErrorMessages,
  onConflict?: () => void,
): void {
  const failure = toApiFailure(err);
  const detail = failure.problem?.detail;

  switch (failure.status) {
    case 412:
      toast.error(detail ?? messages.precondition);
      onConflict?.();
      return;
    case 403:
      toast.error(detail ?? messages.forbidden);
      return;
    case 409:
      toast.error(detail ?? messages.conflict);
      return;
    default:
      toast.error(detail ?? messages.generic);
  }
}
